import { useEffect, useRef, useState } from "react";
import { CloseCircleOutlined, LinkOutlined } from "@ant-design/icons";
import { fetchBusinessAreaDataById, fetchDomainData, type BusinessAreaDataDefinition, type DomainDataEntry } from "./dataSetApi";
import { buildToolError } from "./errorEnvelope";
import type { SchemaFieldSummary, ToolPartProps } from "./types";

interface ResolvePicklistContextInput {
    field: string;
    code?: string;
    description: string;
}

interface PicklistEntry {
    code: string;
    description: string;
    highlighted?: boolean;
}

interface ResolvePicklistContextOutput {
    field: string;
    picklistType?: "static" | "dynamic";
    isPicklist: boolean;
    requestedCode?: string;
    requestedCodeDescription?: string;
    businessAreaData?: {
        domainName?: string;
        businessAreaName?: string;
        domainDescription?: string;
        domainPurpose?: string;
    };
    entries: PicklistEntry[];
    entryCount: number;
    note?: string;
    description: string;
}

function findFieldByName(schemaFields: SchemaFieldSummary[], name: string): SchemaFieldSummary | undefined {
    if (!name) return undefined;
    const lower = name.trim().toLowerCase();
    return (schemaFields || []).find((f) => (f?.name || "").trim().toLowerCase() === lower);
}

export default function ResolvePicklistContextTool({ toolCallId, state, input, output, errorText, helpers }: ToolPartProps<ResolvePicklistContextInput, ResolvePicklistContextOutput>) {
    const { addToolOutput, tenantId, schemaFields } = helpers;
    const resolvedRef = useRef(false);
    const [pending, setPending] = useState(false);

    useEffect(() => {
        if (resolvedRef.current) return;
        if (state !== "input-available") return;
        const fieldName = input?.field;
        if (!fieldName) return;
        resolvedRef.current = true;

        const emit = (out: ResolvePicklistContextOutput) => addToolOutput({ tool: "resolvePicklistContext", toolCallId, output: out });
        const requestedCode = input?.code?.trim() || undefined;
        const description = input?.description || "";

        const field = findFieldByName(schemaFields, fieldName);
        if (!field) {
            emit({ field: fieldName, isPicklist: false, entries: [], entryCount: 0, note: `No field named "${fieldName}" was found in this schema.`, description });
            return;
        }
        const fieldLabel = field.name || fieldName;
        if (field.picklistType !== "static" && field.picklistType !== "dynamic") {
            emit({ field: fieldLabel, isPicklist: false, entries: [], entryCount: 0, note: `The field "${fieldLabel}" is not a picklist, so it has no coded values to resolve.`, description });
            return;
        }

        const finish = (entries: PicklistEntry[], businessAreaData?: ResolvePicklistContextOutput["businessAreaData"], note?: string) => {
            const marked = entries.map((e) => ({ ...e, highlighted: requestedCode ? e.code?.toLowerCase() === requestedCode.toLowerCase() : undefined }));
            const hit = requestedCode ? marked.find((e) => e.highlighted) : undefined;
            emit({
                field: fieldLabel,
                picklistType: field.picklistType,
                isPicklist: true,
                requestedCode,
                requestedCodeDescription: hit?.description,
                businessAreaData,
                entries: marked,
                entryCount: marked.length,
                note,
                description,
            });
        };

        // Static picklist: values live inline on the field, no network call needed.
        if (field.picklistType === "static") {
            const entries = (field.staticPicklist || []).filter((p) => p && p.code).map((p) => ({ code: p.code, description: p.description }));
            finish(entries, undefined, entries.length === 0 ? "This static picklist has no configured values." : undefined);
            return;
        }

        // Dynamic picklist: resolve the linked Business Area Data + its code list.
        if (!field.dataDomainId) {
            emit({ field: fieldLabel, picklistType: "dynamic", isPicklist: true, entries: [], entryCount: 0, note: `The field "${fieldLabel}" is a dynamic picklist but has no linked Business Area Data (data domain).`, description });
            return;
        }
        if (!tenantId) {
            addToolOutput({
                tool: "resolvePicklistContext",
                toolCallId,
                output: buildToolError({ message: "Missing tenant context to resolve the linked Business Area Data." }, "Could not resolve the picklist because the tenant context is incomplete."),
            });
            return;
        }

        setPending(true);
        (async () => {
            try {
                const [domain, domainData]: [BusinessAreaDataDefinition | null, DomainDataEntry[]] = await Promise.all([
                    fetchBusinessAreaDataById(tenantId, field.dataDomainId as string),
                    fetchDomainData(tenantId, field.dataDomainId as string),
                ]);
                const entries = (domainData || []).filter((e) => e && e.code).map((e) => ({ code: e.code, description: e.description }));
                finish(
                    entries,
                    domain
                        ? { domainName: domain.domainName, businessAreaName: domain.businessAreaName, domainDescription: domain.domainDescription, domainPurpose: domain.domainPurpose }
                        : undefined,
                    entries.length === 0 ? "The linked Business Area Data has no data values loaded yet." : undefined,
                );
            } catch (e: any) {
                addToolOutput({
                    tool: "resolvePicklistContext",
                    toolCallId,
                    output: buildToolError(e?.response?.data || e, "Could not load the picklist definition for that field. Please try again or open the Business Area Data screen."),
                });
            } finally {
                setPending(false);
            }
        })();
    }, [state, input, tenantId, schemaFields, addToolOutput, toolCallId]);

    const description = (state === "output-available" && output?.description) || input?.description || "";
    const sample = state === "output-available" && output ? output.entries.slice(0, 8) : [];

    return (
        <div className="rounded-2xl border border-zinc-200 bg-white p-3 text-sm text-zinc-800 shadow-sm">
            <div className="mb-1 flex items-center gap-2 text-[#001D3D]">
                <LinkOutlined />
                <span className="text-xs font-semibold uppercase tracking-wider">Golden thread</span>
            </div>
            {description && <div className="text-xs text-zinc-600">{description}</div>}
            {pending && <div className="mt-1 text-xs text-zinc-500">Resolving picklist definition...</div>}
            {state === "output-available" && output && (
                <div className="mt-1 text-xs text-zinc-500">
                    {!output.isPicklist
                        ? output.note || "Not a picklist field."
                        : output.businessAreaData?.domainName
                          ? `${output.field} → ${output.businessAreaData.domainName} (${output.entryCount} value${output.entryCount === 1 ? "" : "s"})`
                          : `${output.field}: ${output.entryCount} value${output.entryCount === 1 ? "" : "s"}`}
                </div>
            )}
            {state === "output-available" && output?.businessAreaData?.domainDescription && (
                <div className="mt-1 text-[11px] text-zinc-500">{output.businessAreaData.domainDescription}</div>
            )}
            {sample.length > 0 && (
                <ul className="mt-2 flex flex-col gap-1">
                    {sample.map((e, idx) => (
                        <li key={`${e.code}-${idx}`} className={`rounded-md border px-2 py-1 text-[11px] ${e.highlighted ? "border-amber-300 bg-amber-50 text-amber-800" : "border-zinc-200 bg-zinc-50 text-zinc-700"}`}>
                            <span className="font-mono text-[10px] text-zinc-500">{e.code}</span>
                            {e.description ? <span>{" · "}{e.description}</span> : null}
                        </li>
                    ))}
                    {output && output.entryCount > sample.length && <li className="text-[10px] text-zinc-400">...and {output.entryCount - sample.length} more.</li>}
                </ul>
            )}
            {state === "output-error" && (
                <div className="mt-2 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {errorText || "Tool error."}
                </div>
            )}
        </div>
    );
}
